import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, AlertTriangle, Building2, Target } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AppShell } from "@/components/layout/AppShell";
import { EmptyState } from "@/components/intelligence/EmptyState";
import { SeguirCriterioButton } from "@/components/watchlist/SeguirCriterioButton";
import { GastoRealCard } from "@/components/oc/GastoRealCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { competitorsService } from "@/services/competitors.service";
import type { CompetidorVentana } from "@/types";
import { formatCLP } from "@/lib/mock-data";

function NotFoundCompetitor() {
  return (
    <AppShell title="Competidor no encontrado">
      <EmptyState
        icon={AlertTriangle}
        title="No encontramos el competidor"
        description="Puede que no tenga adjudicaciones en la ventana seleccionada."
        action={
          <Link to="/competidores" className="text-sm text-primary hover:underline">
            Volver al ranking
          </Link>
        }
      />
    </AppShell>
  );
}

export const Route = createFileRoute("/competidores/$id")({
  head: () => ({
    meta: [
      { title: "Perfil de competidor — LicitaPro" },
      {
        name: "description",
        content: "Historial de adjudicaciones, categorías dominantes y comportamiento competitivo.",
      },
      { property: "og:title", content: "Perfil de competidor — LicitaPro" },
      { property: "og:description", content: "Análisis competitivo profundo en LicitaPro." },
    ],
  }),
  notFoundComponent: NotFoundCompetitor,
  errorComponent: NotFoundCompetitor,
  component: CompetidorDetalle,
});

const VENTANAS: Array<{ value: CompetidorVentana; label: string }> = [
  { value: "90", label: "Últimos 90 días" },
  { value: "180", label: "Últimos 180 días" },
  { value: "365", label: "Último año" },
  { value: "todo", label: "Todo el histórico" },
];

type TipoCompetidores = "licitacion" | "compra-agil" | "todos";

const TIPOS: Array<{ value: TipoCompetidores; label: string }> = [
  { value: "licitacion", label: "Licitaciones" },
  { value: "compra-agil", label: "Compra Ágil" },
  { value: "todos", label: "Todos" },
];

function CompetidorDetalle() {
  const { id } = Route.useParams();
  const [ventana, setVentana] = useState<CompetidorVentana>("365");
  const [tipo, setTipo] = useState<TipoCompetidores>("licitacion");

  const detalle = useQuery({
    queryKey: ["competidor", id, ventana, tipo],
    queryFn: () => competitorsService.byRut(id, { ventana, tipo }),
    retry: false,
    staleTime: 5 * 60_000,
  });

  if (detalle.isLoading) {
    return (
      <AppShell title="Perfil de competidor">
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-72 rounded-xl" />
        </div>
      </AppShell>
    );
  }

  if (detalle.isError || !detalle.data) return <NotFoundCompetitor />;
  const c = detalle.data;

  return (
    <AppShell
      title={c.nombre}
      subtitle={`RUT ${c.rut} · ${c.adjudicadas} adjudicaciones · ${formatCLP(c.montoAdjudicado)}`}
      actions={
        <div className="flex items-center gap-2">
          <Select value={ventana} onValueChange={(v) => setVentana(v as CompetidorVentana)}>
            <SelectTrigger className="w-44" aria-label="Ventana temporal">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VENTANAS.map((v) => (
                <SelectItem key={v.value} value={v.value}>
                  {v.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={tipo} onValueChange={(v) => setTipo(v as TipoCompetidores)}>
            <SelectTrigger className="w-40" aria-label="Tipo de oportunidad">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIPOS.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <SeguirCriterioButton
            opciones={[{ kind: "competidor", valor: c.nombre, label: "Competidor" }]}
          />
          <Link
            to="/competidores"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" /> Volver
          </Link>
        </div>
      }
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Adjudicadas" value={c.adjudicadas} />
        <Stat label="Participación" value={`${c.participacion}%`} tone="primary" />
        <Stat label="Ticket promedio" value={formatCLP(c.ticketPromedio)} tone="success" />
        <Stat label="Agresividad" value={c.scoreAgresividad} tone="warning" />
      </div>

      <div className="rounded-xl border border-border bg-card p-5 shadow-elegant mb-6">
        <h3 className="font-display font-semibold mb-3">Evolución de adjudicaciones</h3>
        {c.serie.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin datos en la ventana seleccionada.</p>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={c.serie}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="periodo" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={40} />
                <RTooltip
                  formatter={(value: number, name) =>
                    name === "monto" ? formatCLP(value) : `${value} adjudicaciones`
                  }
                />
                <Area
                  type="monotone"
                  dataKey="adjudicadas"
                  stroke="hsl(var(--primary))"
                  fill="hsl(var(--primary) / 0.2)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Section title="Organismos donde gana">
          {c.topOrganismos.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin registros.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {c.topOrganismos.map((o) => (
                <li key={o.nombre} className="flex items-center justify-between gap-2">
                  <span className="truncate text-foreground">{o.nombre}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {o.adjudicadas} · {formatCLP(o.monto)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Categorías dominantes">
          <ChipList items={c.topCategorias.map((x) => `${x.nombre} (${x.adjudicadas})`)} />
        </Section>

        <Section title="Vs. mi empresa">
          <div className="space-y-2 text-sm">
            <Fila
              icon={Target}
              label="Licitaciones donde nos cruzamos"
              value={String(c.vsMiEmpresa.licitacionesEnComun)}
            />
            <Fila
              icon={Building2}
              label="Organismos en común"
              value={c.vsMiEmpresa.organismosEnComun.join(", ") || "Ninguno"}
            />
            <Fila
              icon={Target}
              label="Categorías en común"
              value={c.vsMiEmpresa.categoriasEnComun.join(", ") || "Ninguna"}
            />
          </div>
        </Section>

        <GastoRealCard rut={c.rut} />

        <Section title="Últimas adjudicaciones">
          {c.ultimasAdjudicaciones.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin adjudicaciones registradas.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {c.ultimasAdjudicaciones.map((a) => (
                <li key={a.licitacionId} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <Link
                      to="/licitaciones/$id"
                      params={{ id: a.licitacionId }}
                      className="text-foreground hover:text-primary truncate block"
                    >
                      {a.nombre}
                    </Link>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {a.organismo}
                      {a.fecha && ` · ${new Date(a.fecha).toLocaleDateString("es-CL")}`}
                    </div>
                  </div>
                  <span className="font-mono tabular-nums text-primary whitespace-nowrap">
                    {formatCLP(a.monto)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => detalle.refetch()}>
          Actualizar datos
        </Button>
      </div>
    </AppShell>
  );
}

function Fila({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
        <Icon className="size-3.5" /> {label}
      </span>
      <span className="text-right text-foreground">{value}</span>
    </div>
  );
}

function Stat({
  value,
  label,
  tone,
}: {
  value: string | number;
  label: string;
  tone?: "primary" | "success" | "warning";
}) {
  const cls =
    tone === "primary"
      ? "text-primary"
      : tone === "success"
        ? "text-success"
        : tone === "warning"
          ? "text-warning"
          : "text-foreground";
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-elegant">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-2xl font-display font-semibold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-elegant">
      <h3 className="font-display font-semibold mb-3">{title}</h3>
      {children}
    </div>
  );
}

function ChipList({ items }: { items: string[] }) {
  if (items.length === 0) return <p className="text-sm text-muted-foreground">Sin registros.</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((x) => (
        <span key={x} className="text-[11px] px-2 py-1 rounded-full bg-muted text-muted-foreground">
          {x}
        </span>
      ))}
    </div>
  );
}
